/* ═══════════════════════════════════════════════════════════════════════════
   sealDevices.js — which devices a NEW message is sealed for.

   🟥 EVERY MESSAGE WAS SEALED FOR EVERY DEVICE THE ACCOUNT HAS EVER HAD. A device entry
      (`users/{uid}.devices.{id}`) is never removed: the public key has to stay, because it is
      how messages that device SENT are opened. So the list only grows — and every message
      carried a sealed copy of its whole text (and of any quote) for each entry.
      The web makes the most of them: a log out wipes the browser's key (./wipeLocal),
      so the next log in is a brand-new device, and the old entry — whose key no longer exists
      anywhere — kept receiving a copy of every message, for ever. Twenty entries means one
      reply quoting a 24 KB log weighs ~600 KB, and every phone listening to the chat
      downloads and parses it.

   🟢 THE KEY STAYS; THE COPIES STOP. Nothing is deleted. Opening a message still reads the
      full device map (crypto/e2ee.getDeviceKeys); only SEALING skips a device that
      is known to be gone:
        · a browser that LOGGED OUT (`retiredAt`, written on its way out — its key is wiped)
          and has not been seen since;
        · a browser not seen for DEVICE_IDLE_MS — the same line the mailbox already draws
          (./syncFanout canReceive): one that comes back links again and backfills;
        · a phone with NO push token (the server prunes a dead one; logging out removes it)
          and no sign of life for PHONE_IDLE_MS. A phone that is merely switched off keeps
          its token, so it is never skipped — that is the case that must not lose messages.
      `lastSeenAt` is the sign of life: every client stamps its own entry at most once per
      SEEN_STAMP_EVERY_MS (the app's CryptoService.startOwnDeviceWatch; here crypto/identity).

   🟥 NEVER NONE. If the rules would skip every device a person has, they skip nothing —
      sealing to nobody loses the message for everyone; an extra copy only costs bytes.

   💬 AND A QUOTE IS A QUOTE. A reply sealed the WHOLE text it quoted, once per device.
      The bubble shows the original from memory when it has it (ui quote, `quoteCut`), so
      the sealed quote only needs to stand in when the original is gone: QUOTE_SEAL_MAX
      characters. Twin of the app's services/sealDevices.ts (gtest/sealdevices compares them).
   ═══════════════════════════════════════════════════════════════════════════ */

const DAY = 24 * 60 * 60 * 1000;
/** A browser not seen for this long is not sealed for (= syncFanout.DEVICE_IDLE_MS). */
export const DEVICE_IDLE_MS = 45 * DAY;
/** A phone with no push token and no sign of life for this long is not sealed for. */
export const PHONE_IDLE_MS = 90 * DAY;
/** How often a client re-stamps its own `lastSeenAt`. */
export const SEEN_STAMP_EVERY_MS = 20 * 60 * 60 * 1000;
/** The most of a quoted text sealed into a reply (characters). */
export const QUOTE_SEAL_MAX = 1000;

const ms = (v) => {
  if (typeof v === 'number') return v > 0 ? v : 0;
  if (v && typeof v.toMillis === 'function') { try { return Number(v.toMillis()) || 0; } catch { return 0; } }
  if (v && typeof v.seconds === 'number') return v.seconds * 1000;
  if (v instanceof Date) return v.getTime() || 0;
  return 0;
};

const isBrowser = (d) => /^web/.test(String(d?.platform || '').trim().toLowerCase());

/** The last sign of life a device entry carries (ms; 0 = none). */
export function lastSignOfLife(d) {
  if (!d || typeof d !== 'object') return 0;
  return Math.max(ms(d.lastSeenAt), ms(d.fcmTokenUpdatedAt), ms(d.updatedAt));
}

/** Why a device is not sealed for, or null when it is. */
/**
 * 🔐 HAS THIS DEVICE LEFT THE ACCOUNT — decided from its entry alone, never from the clock: revoked
 * (logged out from another device) or logged out itself and not seen since. The security code and
 * the "security code changed" notice count only the devices that have not (an idle browser is still
 * a key that can read; a clock-based answer would make two phones disagree about the code).
 */
export function departed(d) {
  if (!d || typeof d !== 'object') return true;
  if (ms(d.revokedAt) > 0) return true;
  const out = ms(d.retiredAt);
  return out > 0 && out >= ms(d.lastSeenAt);
}

export function retiredReason(d, now) {
  if (!d || typeof d !== 'object' || !d.publicKey) return null;   // no key: never in the map anyway
  if (ms(d.revokedAt) > 0) return 'revoked';                     // 🔐 logged out FROM ANOTHER DEVICE — never sealed for again, push token or not
  const seen = lastSignOfLife(d);
  const out = ms(d.retiredAt);
  if (out > 0 && out >= ms(d.lastSeenAt)) return 'logged out';
  if (isBrowser(d)) {
    /* a browser with no stamp at all is NEW, not ancient (see syncFanout.canReceive) */
    if (seen > 0 && now - seen > DEVICE_IDLE_MS) return 'browser idle';
    return null;
  }
  if (d.fcmToken || d.voipToken) return null;                     // reachable: never skipped
  if (seen > 0 && now - seen > PHONE_IDLE_MS) return 'phone gone';
  return null;
}

/** 🔐 The entries that share a push token with a newer entry of the same account (the stale ids). */
export function duplicateTokenIds(devices) {
  const byToken = new Map();
  for (const id of Object.keys(devices || {})) {
    const d = devices[id];
    if (!d || !d.publicKey) continue;
    const tok = String(d.fcmToken || d.voipToken || '');
    if (!tok) continue;
    const arr = byToken.get(tok) || [];
    arr.push(id);
    byToken.set(tok, arr);
  }
  const out = [];
  for (const arr of byToken.values()) {
    if (arr.length < 2) continue;
    const fresh = (id) => Math.max(lastSignOfLife(devices[id]), ms(devices[id].updatedAt), ms(devices[id].registeredAt));
    const newest = arr.slice().sort((a, b) => fresh(b) - fresh(a) || (a < b ? -1 : 1))[0];
    for (const id of arr) if (id !== newest) out.push(id);
  }
  return out;
}

/**
 * The device ids NOT to seal for, out of one person's device map.
 * @param keep ids that are always sealed for (this device)
 */
export function sealSkips(devices, now, keep = []) {
  const ids = Object.keys(devices || {}).filter((id) => !!devices[id]?.publicKey);
  const skip = ids.filter((id) => !keep.includes(id) && retiredReason(devices[id], now) != null);
  /* 🔐 ONE PHONE, TWO ENTRIES. A phone whose device id was minted again (a data clear, a restore)
     keeps its push token, so its old entry stayed "reachable" and was sealed for for ever — a copy
     of every message to a key nothing holds any more. Entries sharing a push token are ONE phone:
     only the one seen last is sealed for. */
  for (const id of duplicateTokenIds(devices)) if (!keep.includes(id) && !skip.includes(id)) skip.push(id);
  if (skip.length >= ids.length) return [];                       // 🟥 never none
  return skip;
}

/** Is this device's own `lastSeenAt` due for a re-stamp? */
export function seenStampDue(d, now) {
  if (!d || typeof d !== 'object' || !d.publicKey) return false;   // never create a half entry
  const last = ms(d.lastSeenAt);
  return !(last > 0 && now - last < SEEN_STAMP_EVERY_MS);
}

/** The quoted text a reply seals: at most QUOTE_SEAL_MAX characters. */
export function capQuote(text) {
  const s = typeof text === 'string' ? text : '';
  if (s.length <= QUOTE_SEAL_MAX) return { text: s, cut: false };
  /* never split a surrogate pair (an emoji cut in half renders as a box) */
  let end = QUOTE_SEAL_MAX;
  const c = s.charCodeAt(end - 1);
  if (c >= 0xd800 && c <= 0xdbff) end -= 1;
  return { text: `${s.slice(0, end)}…`, cut: true };
}
